import { useEffect } from 'react'
import { crud } from '@think-internet/zeus-frontend-package'
import { Survey, SurveyContext, SurveyType } from '../../../../../types/Survey/Survey'
import props from '../../../../../redux/props'
import { Concept, ConceptConfiguration, ConceptDataCluster, ConceptValues } from '../../../../../types/Survey/Analysis/Concept'
import { useDispatch, useSelector } from 'react-redux'
import { setLocal } from '../../../../../redux/action/local'
import useToast from '../../../../hooks/useToast'
import useCompleteConceptUnits from './useCompleteConceptUnits'
import { getSumOfConcept } from '../../../../hooks/useAnalysisCalculation/calculateResults'
import _ from 'lodash'
import { PrebuildType } from '../../../../../types/Survey/Element'
import { getUUID } from '../../../../../utility'
import { SplitOption } from '../../../../../types/Survey/Analysis/Calculation'

type SetActiveConcept = (uuid: string) => void

type UseConceptConfiguration = (contextUUID?: string) => {
  context: SurveyContext
  concepts: Concept[]
  getActiveConcept: () => Concept
  activeConceptUUID: string
  setActiveConcept: SetActiveConcept
  addConcept: AddConcept
  removeConcept: RemoveConcept
  updateActiveConcept: UpdateActiveConcept
  updateActiveConceptUnit: UpdateActiveConceptUnit
  teamSurvey: Survey
  managerSurvey: Survey
  leaderSurvey: Survey
  graphConceptUUIDList: string[]
  toggleGraphConcept: ToggleGraphConcept
  loading: boolean
  conceptDataCluster: ConceptDataCluster
  copyUnitValues: CopyUnitValues
  removeUnitValues: RemoveUnitValues
  updateConceptOrder: UpdateConceptOrder
}

type AddConcept = (concept: Concept | Concept[]) => void
type RemoveConcept = (uuid: string) => void

type UpdateActiveConcept = (concept: Concept) => void

type UpdateActiveConceptUnit = (uuid: string) => (values: ConceptValues) => void

type ToggleGraphConcept = (uuid: string) => () => void

type CopyUnitValues = (uuid: string, name: string, mitarbeiterAmount: number, splitOption: SplitOption) => void

type RemoveUnitValues = (uuid: string, targetUUID: string) => void

type UpdateConceptOrder = (concepts: Concept[]) => void

const useConceptConfiguration: UseConceptConfiguration = (contextUUID) => {
  const t = useSelector((s) => s[props.TRANSLATION])
  const toast = useToast()
  const dispatch = useDispatch()
  const surveyConceptFeature = crud.use(props.SURVEY_ANALYSIS_CONCEPT)
  const surveyContextFeature = crud.use(props.SURVEY_CONTEXT)
  const surveyFeature = crud.use(props.SURVEY)
  const conceptConfiguration: ConceptConfiguration = useSelector((s) => s[props.SURVEY_ANALYSIS_CONCEPT_CONFIGURATION])
  const cluster: ConceptDataCluster = useSelector((s) => s[props.SURVEY_ANALYSIS_CONCEPT_DATA_CLUSTER])
  const { run: runCompletion, init: initCompletion } = useCompleteConceptUnits()

  useEffect(() => {
    const fetchConcepts = async () => {
      const context: SurveyContext = await surveyContextFeature.get({ uuid: contextUUID })
      const concepts: Concept[] = await surveyConceptFeature.list({ surveyContextUUID: context.uuid })

      if (context && Array.isArray(concepts) && cluster) {
        const completedConcepts = []
        for (const concept of concepts) {
          const completedConcept = await runCompletion(concept)
          completedConcepts.push(completedConcept)
        }
        const newConfig: ConceptConfiguration = {
          ...conceptConfiguration,
          context,
          concepts: completedConcepts,
          graphConceptUUIDList: concepts.map((c) => c.uuid),
          teamSurvey: cluster.teamSurvey,
          managerSurvey: cluster.managerSurvey,
          leaderSurvey: cluster.leaderSurvey,
        }
        if (concepts.length > 0) {
          newConfig.activeConceptUUID = concepts[0].uuid
        }
        dispatch(setLocal(props.SURVEY_ANALYSIS_CONCEPT_CONFIGURATION, newConfig))
      }
    }
    if (!conceptConfiguration && !!contextUUID && cluster) {
      fetchConcepts()
    }
  }, [conceptConfiguration, contextUUID, dispatch, surveyContextFeature, surveyConceptFeature, surveyFeature, runCompletion, initCompletion, cluster])

  useEffect(() => {
    const initialize = async () => {
      const surveys: Survey[] = await surveyFeature.list({ surveyContextUUID: contextUUID })
      const getSurvey = (surveyType: SurveyType) => surveys.find((s) => s.surveyType === surveyType)

      const teamSurvey = getSurvey(SurveyType.TEAM)
      const managerSurvey = getSurvey(SurveyType.MANAGER)
      const leaderSurvey = getSurvey(SurveyType.LEADER)

      await initCompletion(teamSurvey, managerSurvey, leaderSurvey)
    }
    if (!cluster && !!contextUUID) {
      initialize()
    }
  })

  const getActiveConcept = () => {
    return !!conceptConfiguration?.concepts ? conceptConfiguration.concepts.find((c) => c.uuid === conceptConfiguration.activeConceptUUID) : null
  }

  const setActiveConcept: SetActiveConcept = (uuid: string) => {
    const newConfig: ConceptConfiguration = {
      ...conceptConfiguration,
      activeConceptUUID: uuid,
    }
    dispatch(setLocal(props.SURVEY_ANALYSIS_CONCEPT_CONFIGURATION, newConfig))
  }

  const addConcept: AddConcept = async (concept: Concept | Concept[]) => {
    if (Array.isArray(concept)) {
      const completedConcepts = await Promise.all(concept.map((c) => runCompletion(c)))
      const newConfig = {
        ...conceptConfiguration,
        concepts: [...conceptConfiguration.concepts, ...completedConcepts],
        graphConceptUUIDList: [...conceptConfiguration.graphConceptUUIDList, ...completedConcepts.map((c) => c.uuid)],
      }
      dispatch(setLocal(props.SURVEY_ANALYSIS_CONCEPT_CONFIGURATION, newConfig))
    } else {
      const completedConcept = await runCompletion(concept)
      const newConfig = {
        ...conceptConfiguration,
        concepts: [...conceptConfiguration.concepts, completedConcept],
        graphConceptUUIDList: [...conceptConfiguration.graphConceptUUIDList, completedConcept.uuid],
      }
      dispatch(setLocal(props.SURVEY_ANALYSIS_CONCEPT_CONFIGURATION, newConfig))
    }
  }

  const removeConcept: RemoveConcept = async (uuid: string) => {
    const status = await surveyConceptFeature.remove({ uuid })
    if (!!status) {
      const newConceptList = conceptConfiguration.concepts.filter((c) => c.uuid !== uuid)
      const newConfig = {
        ...conceptConfiguration,
        concepts: newConceptList,
      }
      if (newConceptList.length > 0) {
        newConfig.activeConceptUUID = newConceptList[0].uuid
      }
      dispatch(setLocal(props.SURVEY_ANALYSIS_CONCEPT_CONFIGURATION, newConfig))
    }
  }

  const updateActiveConcept: UpdateActiveConcept = async (concept: Concept) => {
    const updatedConcepts = await Promise.all(
      conceptConfiguration.concepts.map((c) => {
        if (c.uuid === concept.uuid) {
          return runCompletion(concept)
        }
        return c
      }),
    )

    const newConfig = {
      ...conceptConfiguration,
      concepts: updatedConcepts,
    }
    dispatch(setLocal(props.SURVEY_ANALYSIS_CONCEPT_CONFIGURATION, newConfig))

    // update the concept in the database
    const updateConceptPayload: Concept = {
      ...concept,
      units: Object.keys(concept.units)
        .filter((u) => u !== 'sum')
        .reduce((acc, unitUUID) => {
          const unit: ConceptValues = concept.units[unitUUID]
          const newUnit: ConceptValues = {
            unitUUID,
            name: unit.name,
            originUnitUUID: unit.originUnitUUID,
            splitOption: unit.splitOption,
            productionDistance: unit.productionDistance,
            originalMitarbeiter: unit.originalMitarbeiter,
            anwesenheitPercentage: unit.anwesenheitPercentage,
            desksharingPercentage: unit.desksharingPercentage,
            [PrebuildType.STAUFLAECHE]: unit[PrebuildType.STAUFLAECHE],
            [PrebuildType.SONDERBEDARFE]: unit[PrebuildType.SONDERBEDARFE],
            [PrebuildType.EINZELBUEROS]: unit[PrebuildType.EINZELBUEROS],
            [PrebuildType.WORKSTATIONS]: unit[PrebuildType.WORKSTATIONS],
            drucker: unit.drucker,
            locker: unit.locker,
            workcafe: unit.workcafe,
            garderobe: unit.garderobe,
            mitarbeiter: unit.mitarbeiter,
            factor: unit.factor,
            bueroType: unit.bueroType,
            optimizationMode: unit.optimizationMode,
          }
          acc[unitUUID] = newUnit
          return acc
        }, {}),
    }
    const status = await surveyConceptFeature.update(updateConceptPayload)
    if (!status) {
      toast(t.survey.analysis.concept.update.error)
    }
  }

  const updateActiveConceptUnit: UpdateActiveConceptUnit = (uuid: string) => (values: ConceptValues) => {
    const conceptCopy = _.cloneDeep(getActiveConcept())
    const updatedValues = _.cloneDeep(values)
    conceptCopy.units[uuid] = updatedValues
    conceptCopy.units.sum = getSumOfConcept(cluster.teamSurvey.context, conceptCopy, conceptCopy.units)
    updateActiveConcept(conceptCopy)
  }

  const toggleGraphConcept: ToggleGraphConcept = (uuid: string) => () => {
    const newConfig = {
      ...conceptConfiguration,
      graphConceptUUIDList: conceptConfiguration.graphConceptUUIDList.includes(uuid)
        ? conceptConfiguration.graphConceptUUIDList.filter((u) => u !== uuid)
        : [...conceptConfiguration.graphConceptUUIDList, uuid],
    }
    dispatch(setLocal(props.SURVEY_ANALYSIS_CONCEPT_CONFIGURATION, newConfig))
  }

  const copyUnitValues: CopyUnitValues = (uuid: string, name: string, mitarbeiterAmount: number, splitOption: SplitOption) => {
    const conceptCopy = _.cloneDeep(getActiveConcept())

    // adjust the origin unit
    const originUnit = conceptCopy.units[uuid]
    originUnit.mitarbeiter = originUnit.mitarbeiter - mitarbeiterAmount
    originUnit.originalMitarbeiter = originUnit.originalMitarbeiter - mitarbeiterAmount

    // create a new unit
    const newUnit = _.cloneDeep(conceptCopy.units[uuid])
    newUnit.EINZELBUEROS = {
      amount: 0,
      originalAmount: 0,
      practicalQM: 0,
    }
    newUnit.STAUFLAECHE = {
      originalPracticalQM: 0,
      practicalQM: 0,
    }
    newUnit.SONDERBEDARFE = []
    newUnit.WORKSTATIONS = {
      amount: 0,
      originalAmount: 0,
    }
    newUnit.drucker = {
      originalPracticalQM: 0,
      practicalQM: 0,
    }
    newUnit.locker = {
      originalPracticalQM: 0,
      practicalQM: 0,
    }
    newUnit.workcafe = {
      originalPracticalQM: 0,
      practicalQM: 0,
    }
    newUnit.garderobe = {
      originalPracticalQM: 0,
      practicalQM: 0,
    }
    newUnit.name = name
    newUnit.unitUUID = getUUID()
    newUnit.originUnitUUID = originUnit.originUnitUUID
    newUnit.splitOption = splitOption
    newUnit.productionDistance = originUnit.productionDistance
    newUnit.mitarbeiter = mitarbeiterAmount
    newUnit.originalMitarbeiter = mitarbeiterAmount

    // add the new unit to the concept
    conceptCopy.units[newUnit.unitUUID] = newUnit

    // updated the origin unit
    conceptCopy.units[originUnit.unitUUID] = originUnit

    // update the active concept
    updateActiveConcept(conceptCopy)
  }

  const removeUnitValues: RemoveUnitValues = (uuid: string, maReceiverUnitUUID: string) => {
    const conceptCopy = _.cloneDeep(getActiveConcept())
    const sourceUnit = conceptCopy.units[uuid]
    const receiverUnit = conceptCopy.units[maReceiverUnitUUID]
    receiverUnit.mitarbeiter = receiverUnit.mitarbeiter + sourceUnit.mitarbeiter
    receiverUnit.originalMitarbeiter = receiverUnit.originalMitarbeiter + sourceUnit.originalMitarbeiter
    delete conceptCopy.units[uuid]
    conceptCopy.floorMapping = conceptCopy.floorMapping.filter((mapping) => mapping.targetUUID !== uuid)
    updateActiveConcept(conceptCopy)
  }

  const updateConceptOrder: UpdateConceptOrder = async (concepts: Concept[]) => {
    const newConfig = {
      ...conceptConfiguration,
      concepts: concepts.map((c, index) => ({ ...c, index })),
    }
    dispatch(setLocal(props.SURVEY_ANALYSIS_CONCEPT_CONFIGURATION, newConfig))
    for (const concept of newConfig.concepts) {
      const status = await surveyConceptFeature.update({ ...concept, index: concept.index })
      if (!status) {
        toast(t.survey.analysis.concept.update.error)
      }
    }
  }

  return {
    context: conceptConfiguration?.context || null,
    concepts: conceptConfiguration?.concepts || null,
    getActiveConcept,
    activeConceptUUID: conceptConfiguration?.activeConceptUUID || null,
    loading: conceptConfiguration === null,
    setActiveConcept,
    addConcept,
    removeConcept,
    updateActiveConcept,
    updateActiveConceptUnit,
    conceptDataCluster: cluster || null,
    teamSurvey: conceptConfiguration?.teamSurvey || null,
    managerSurvey: conceptConfiguration?.managerSurvey || null,
    leaderSurvey: conceptConfiguration?.leaderSurvey || null,
    graphConceptUUIDList: conceptConfiguration?.graphConceptUUIDList || [],
    toggleGraphConcept,
    copyUnitValues,
    removeUnitValues,
    updateConceptOrder,
  }
}

export default useConceptConfiguration
